(ns ycweb.app.website.myaccount.reviews.common
  (:require
   [rum.core :as rum]
   [ycweb.util.core :as u]
   [ycweb.app.website.common :as web-common]
   [ycweb.util.date :as ud]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.headings :as headings]
   [ycweb.components.forms :as input-forms]
   [util.string :as str]))

(rum/defc star-selector < rum/reactive [val-atom idx [stars description]]
  (let [props (input-forms/radiobox-props val-atom stars "star-selector" {})]
    [:label.radio-label.form-field
     {:on-click (:on-click props)}
     [:input.radio props]
     [:span.radio__graphic-wrapper
      [:span.radio__graphic]]
     [:span.radio__text
      [:span.radio__image
       [:span {:class (u/bem :star-rating [:xlarge (str (* stars 10))])}]]
      [:span.radio__description
       [:b (str stars " Stars: ")]
       description]]]))

(def star-desc
  [[5 "You think the product is excellent and believe that others should definitely buy it."]
   [4 "You think the product is good and believe others should buy it."]
   [3 "You think the product is okay and are indifferent about whether or not others should buy it."]
   [2 "You think the product is bad and believe others should think twice before buying it."]
   [1 "You think the product is terrible and feel strongly that no-one should buy it."]])

(defn- reviewer-name
  [{server-name :user/reviewer-name :user/keys [first-name last-name]}]
  (or
    (u/presence server-name)
    (let [last-initial (when (u/presence last-name) (str (subs last-name 0 1) "."))]
      (u/presence (str/join " " (remove str/blank? [first-name last-initial]))))))

(defn- purchase-age
  "Bucketed `, X days/months after purchase` suffix per PHP reviewblockloop.tpl:38-51.
   Returns nil unless we have a purchase date and the review is at least 1 day later."
  [date-added order-placed]
  (when (and date-added order-placed)
    (let [secs (- (ud/php-timestamp date-added) (ud/php-timestamp order-placed))
          days (long (/ secs 86400))]
      (cond
        (and (>= days 1) (<= days 31))   (str ", " days " days after purchase")
        (and (> days 31) (< days 60))    ", 1 month after purchase"
        (and (>= days 60) (< days 90))   ", 2 months after purchase"
        (and (>= days 90) (< days 180))  ", 3 months after purchase"
        (and (>= days 180) (< days 365)) ", over 6 months after purchase"
        (> days 365)                     ", over 1 year after purchase"))))

(defn review-block [review-item & [{:keys [on-helpful-vote admin-edit?]}]]
  (let [{:review/keys [id date-added order-placed review recommend verified-owner? user-helpful? stars
                       helpful-count total-helpful-count user-is-reviewer? review-reply]
         :item-line/keys [variation user]} review-item
        {:variation/keys [feature1 feature2 not-available]} variation
        {:user/keys [admin-user?]} user
        recommend? (not (zero? recommend))]
    [:article.review {:id (str "review-" (:ent/uuid review-item))}
     [:div.review__content
      [:header.review__header
       [:div.review__masthead
        [:div.review__stars
         [:div.star-rating {:class (str "star-rating--" (* stars 10))}]
         [:span.u-hidden-from-view
          stars " out of 5 stars"]]
        (headings/heading (:ent/display-name review-item) {:type :h2 :size :medium :class :review__title})
        [:div.review__meta
         [:div.review__meta-hero
          [:div.review__meta-image
           (web-common/user-avatar user)
           (when admin-user?
             [:div.review__author-icon.review__author-icon--staff
              (buttons/button-icon {:icon :yc-icon-pink})])
           (when verified-owner?
             [:div.review__author-icon.review__author-icon--verified
              (buttons/button-icon {:icon :check :icon-size :tiny :icon-color :light})])]]
         [:div.review__meta-text
          [:div.review__meta-details
           (when (or (u/presence feature1) (u/presence feature2))
             [:p.review__variation
              [:b "Item reviewed: "
               (str/join " | " (remove str/blank? [feature1 feature2]))
               (when (= not-available "Discontinued") " (no longer available)")]])
           (let [formatted-date (ud/date-format (ud/coerce-date date-added) "d MMMM yyyy")
                 age-suffix (when verified-owner? (purchase-age date-added order-placed))]
             [:p.review__author
              "Review by " (or (reviewer-name user) "Anonymous")
              [:span " on "
               [:span.clip-date.clip-date--t.unclip-date--l {:data-date formatted-date}
                [:span.clip-date__text formatted-date]]]
              age-suffix])]
          (cond
            admin-user?
            [:small.review__type
             [:span.text-pill.text-pill--info "Staff Review"]]
            verified-owner?
            [:small.review__type
             [:span.text-pill.text-pill--success "Verified Owner"]])]]]]
      [:p.yc-paragraph.review__body (when (> (count review) 400) {:class [:collapsible-text :js-collapsible-text]})
       (web-common/read-more review)]

      (when (u/presence recommend)
        [:p.yc-paragraph.review__recommend
         [:i {:class (u/bem :icon [(if recommend? :thumbs-up :thumbs-down) :is-dark :small :inline :in-copy-left])}]
         [:span "I would " (when-not recommend? "not ") "recommend this product" [:span.clip-text.unclip-text--s " to a friend"] "."]])

      (cond
        (or user-helpful? (false? user-helpful?))
        [:p.review__helpful.yc-paragraph
         [:span.review__helpful-text
          (str helpful-count " out of " total-helpful-count " people found this review helpful. You found this review " (if user-helpful? "helpful" "unhelpful") ".")]]

        (and on-helpful-vote (not user-is-reviewer?))
        [:p.review__helpful.yc-paragraph
         [:span.review__helpful-text
          (if (and total-helpful-count (pos? total-helpful-count))
            (str helpful-count " out of " total-helpful-count " people found this review helpful. Did you?")
            "Did you find this review helpful?")]
         [:span.review__helpful-options
          [:a.button.button--small.button--raised.button--secondary.button--has-icon-left
           {:href "#" :rel "nofollow"
            :on-click (fn [e] (.preventDefault e) (on-helpful-vote id true))}
           [:span.button__text.u-text-mint-green
            [:i.icon.icon--check.icon--is-dark.icon--default.icon--block.button__icon] "Yes"]]
          [:a.button.button--small.button--raised.button--secondary.button--has-icon-left
           {:href "#" :rel "nofollow"
            :on-click (fn [e] (.preventDefault e) (on-helpful-vote id false))}
           [:span.button__text.u-text-red
            [:i.icon.icon--close.icon--is-dark.icon--default.icon--block.button__icon] "No"]]]]

        (not user-is-reviewer?)
        [:p.review__helpful.yc-paragraph
         (when (and total-helpful-count (pos? total-helpful-count))
           [:span.review__helpful-text
            (str helpful-count " out of " total-helpful-count " people found this review helpful.")])])

      (when (u/presence review-reply)
        [:div.review__reply.group
         [:header.review__header.group.ie-group
          [:div.review__header-image (buttons/button-icon {:icon :yc-icon-pink :icon-size :large})]
          [:div.review__header-text
           [:div.review__meta
            [:div.review__meta-text
             [:p.review__author [:b "Reply by Yuppiechef"]]]
            [:small.review__type
             [:span.text-pill.text-pill--info "Admin Reply"]]]]]
         [:p.review__body.yc-paragraph {:itemProp "reviewBody"}
          review-reply]])

      (when admin-edit?
        [:div.u-margin-top-20.u-text-right
         [:a.button.button--raised.button--default.button--primary
          {:href (str "/admin/reviews.htm?action=edit&id=" id)
           :target "_blank"}
          [:span.button__text "Edit Review"]]])]]))
